import clsx from "clsx";
import {
  Activity,
  BarChart3,
  BookOpen,
  Bot,
  Boxes,
  Building2,
  Calculator,
  Code2,
  Cpu,
  FileClock,
  GitBranch,
  LayoutDashboard,
  ListTree,
  LogOut,
  MessagesSquare,
  Moon,
  Server,
  Settings,
  Sun,
  ThumbsUp,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  perm?: string;
}

const NAV: { title: string; items: NavItem[] }[] = [
  { title: "", items: [{ to: "/", label: "Genel Bakış", icon: LayoutDashboard }] },
  {
    title: "Katalog",
    items: [
      { to: "/catalog", label: "Ürünler ve Ekranlar", icon: Boxes, perm: "catalog.read" },
      { to: "/knowledge", label: "Bilgi Bankası", icon: BookOpen, perm: "knowledge.read" },
    ],
  },
  { title: "Müşteriler", items: [{ to: "/customers", label: "Müşteriler ve Abonelikler", icon: Building2, perm: "customers.read" }] },
  {
    title: "AI Altyapısı",
    items: [
      { to: "/ai/providers", label: "Sağlayıcılar", icon: Server, perm: "ai.read" },
      { to: "/ai/models", label: "Model Kataloğu", icon: Cpu, perm: "ai.read" },
      { to: "/ai/policies", label: "Yönlendirme Politikaları", icon: GitBranch, perm: "ai.read" },
      { to: "/ai/simulator", label: "Maliyet Simülatörü", icon: Calculator, perm: "ai.read" },
    ],
  },
  {
    title: "Asistan Operasyonları",
    items: [
      { to: "/assistant/playground", label: "Playground", icon: Bot, perm: "assistant.playground" },
      { to: "/assistant/conversations", label: "Konuşmalar", icon: MessagesSquare, perm: "conversations.read" },
      { to: "/assistant/runs", label: "Çalıştırma İzleri", icon: Activity, perm: "conversations.read" },
      { to: "/assistant/feedback", label: "Geri Bildirimler", icon: ThumbsUp, perm: "conversations.read" },
    ],
  },
  { title: "Raporlar", items: [{ to: "/usage", label: "Kullanım ve Maliyet", icon: BarChart3, perm: "usage.read" }] },
  {
    title: "Yönetim",
    items: [
      { to: "/admin/users", label: "Sistem Kullanıcıları", icon: Users, perm: "iam.read" },
      { to: "/admin/audit", label: "Denetim Kayıtları", icon: FileClock, perm: "audit.read" },
      { to: "/admin/settings", label: "Platform Ayarları", icon: Settings, perm: "settings.read" },
    ],
  },
];

function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme ?? "light");
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem("dlv-theme", next);
    } catch {
      /* ignore */
    }
    setTheme(next);
  };
  return [theme, toggle];
}

export function AppShell() {
  const { user, logout, can } = useAuth();
  const [theme, toggleTheme] = useTheme();
  return (
    <div className="flex h-full flex-col">
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-line bg-surface px-4">
        <div className="flex items-center gap-2.5">
          <img src="/favicon.svg" alt="" className="h-6 w-6" />
          <span className="text-sm font-semibold">Dataliva AI Gateway</span>
          <span className="hidden text-xs text-ink-3 sm:inline">· Yönetim Konsolu</span>
        </div>
        <div className="flex items-center gap-1">
          <button className="rounded p-1.5 text-ink-2 hover:bg-subtle" onClick={toggleTheme} title="Tema" aria-label="Temayı değiştir">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <div className="mx-2 hidden text-right sm:block">
            <div className="text-xs font-medium leading-4">{user?.full_name}</div>
            <div className="text-2xs leading-4 text-ink-3">{user?.roles.join(", ")}</div>
          </div>
          <button className="rounded p-1.5 text-ink-2 hover:bg-subtle" onClick={logout} title="Çıkış" aria-label="Çıkış yap">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1">
        <nav className="hidden w-60 shrink-0 overflow-y-auto border-r border-line bg-surface py-3 scroll-thin md:block">
          {NAV.map((group) => {
            const items = group.items.filter((i) => !i.perm || can(i.perm));
            if (!items.length) return null;
            return (
              <div key={group.title || "root"} className="mb-3">
                {group.title && <div className="px-4 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wide text-ink-3">{group.title}</div>}
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      clsx(
                        "relative mx-2 flex h-8 items-center gap-2.5 rounded px-2.5 text-sm",
                        isActive ? "bg-brand-soft font-medium text-brand" : "text-ink-2 hover:bg-subtle hover:text-ink",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute -left-2 top-1.5 h-5 w-[3px] rounded-r bg-brand" />}
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
          <div className="mx-4 mt-6 border-t border-line pt-3 text-2xs text-ink-3">
            <a className="block hover:text-brand" href="/developers" target="_blank" rel="noreferrer">
              <Code2 className="mr-1 inline h-3 w-3" />
              Geliştirici portalı
            </a>
            <a className="mt-1.5 block hover:text-brand" href="/docs" target="_blank" rel="noreferrer">
              <ListTree className="mr-1 inline h-3 w-3" />
              API referansı (Swagger)
            </a>
          </div>
        </nav>
        <main className="min-w-0 flex-1 overflow-y-auto scroll-thin">
          <div className="mx-auto max-w-[1440px] px-6 py-5">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
